import type { DefaultNamespace, KeyPrefix, ParseKeys } from '@if/core/i18n'
import { EMonthlyPaymentAgentOverrideMargin, EPaymentPlan, EProvince } from '@if/core/types'
import { FinancingConfigDto } from '@src/data/types/FinancingConfigDto'
import { FundingComputedDto } from '@src/data/types/FundingComputedDto'
import { ProvinceConfigs } from '@src/data/types/ProvinceConfigs'
import { createContext, useContext, useEffect, useId, useMemo, useRef, useSyncExternalStore } from 'react'
import { Control, FieldPath, FieldValues, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

type TranslationKeyPrefix = KeyPrefix<DefaultNamespace>
// Empty `{}` is i18next's documented default for the TOpt arg — required to reach the KPrefix generic.
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
type KeyUnderPrefix<KP extends TranslationKeyPrefix> = ParseKeys<DefaultNamespace, {}, KP>

// Loading registry. Multi-source: an id is loading iff at least one source reports true,
// so a field can expose its own API loading AND inherited prereq-loading under the same id.
// Selective subscription via useSyncExternalStore: writes only re-render consumers of that id.
export interface LoadingStore {
  subscribe: (listener: () => void) => () => void
  get: (id: string) => boolean
  set: (id: string, isLoading: boolean, source: string) => void
  isAnyLoading: () => boolean
}

const NOOP_LOADING_STORE: LoadingStore = {
  subscribe: () => () => {},
  get: () => false,
  set: () => {},
  isAnyLoading: () => false,
}

export const LoadingStoreContext = createContext<LoadingStore>(NOOP_LOADING_STORE)

export const createLoadingStore = (): LoadingStore => {
  const sources: Record<string, Record<string, boolean>> = {}
  const loadingById: Record<string, boolean> = {}
  let anyLoading = false
  const listeners = new Set<() => void>()

  const recomputeAny = (): void => {
    let any = false
    for (const id in loadingById) {
      if (loadingById[id]) {
        any = true
        break
      }
    }
    anyLoading = any
  }

  return {
    subscribe: (listener) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    get: (id) => !!loadingById[id],
    set: (id, isLoading, source) => {
      const cur = sources[id] ?? (sources[id] = {})
      if (cur[source] === isLoading) return
      cur[source] = isLoading
      let next = false
      for (const k in cur) {
        if (cur[k]) {
          next = true
          break
        }
      }
      if (loadingById[id] === next) return
      loadingById[id] = next
      recomputeAny()
      listeners.forEach((l) => l())
    },
    isAnyLoading: () => anyLoading,
  }
}

export const useReportFieldLoading = (id: string, isLoading: boolean) => {
  const store = useContext(LoadingStoreContext)
  const source = useId()
  useEffect(() => {
    if (!id) return
    store.set(id, isLoading, source)
    return () => store.set(id, false, source)
  }, [store, id, isLoading, source])
}

export const useFieldLoading = (id: string): boolean => {
  const store = useContext(LoadingStoreContext)
  const getSnapshot = () => store.get(id)
  return useSyncExternalStore(store.subscribe, getSnapshot)
}

// Returns a Record reference that stays stable while the relevant slice is unchanged,
// so consumer useMemos don't re-fire on unrelated loading flips.
const useLoadingFor = (ids: readonly string[]): Record<string, boolean> => {
  const store = useContext(LoadingStoreContext)
  const idsKey = ids.join('|')
  const cacheRef = useRef<{ key: string; value: Record<string, boolean> }>({ key: '', value: {} })

  const getSnapshot = (): Record<string, boolean> => {
    const next: Record<string, boolean> = {}
    for (const id of ids) next[id] = store.get(id)
    const cache = cacheRef.current
    if (cache.key === idsKey) {
      let same = true
      for (const id of ids) {
        if (cache.value[id] !== next[id]) {
          same = false
          break
        }
      }
      if (same) return cache.value
    }
    cacheRef.current = { key: idsKey, value: next }
    return next
  }

  return useSyncExternalStore(store.subscribe, getSnapshot)
}

export const isPromoPlan = (paymentPlanId: string | undefined): boolean =>
  !!paymentPlanId && paymentPlanId !== (EPaymentPlan.RegularDailyInterests as string)

// RegularDailyInterests scales by a multiplicative margin; promo plans add a flat margin.
// Returns the original value untouched when no plan is selected.
export const getMaxPmtAmountWithAgentLeeway = (
  paymentPlanId: string | undefined,
  maxPmtAmount: number,
): number => {
  if (paymentPlanId === (EPaymentPlan.RegularDailyInterests as string)) {
    return Math.round(maxPmtAmount * EMonthlyPaymentAgentOverrideMargin.RegularDailyInterests * 100) / 100
  }
  if (paymentPlanId) {
    return maxPmtAmount + EMonthlyPaymentAgentOverrideMargin.PromoCode
  }
  return maxPmtAmount
}

// Discriminated by `labelKey`: when omitted, `name` doubles as the translation key
// (validated against the i18n key union to catch typos at call sites).
export type FieldPrerequisiteSpec<
  T extends FieldValues,
  KP extends TranslationKeyPrefix = 'worksheetCommon',
> =
  | {
      name: keyof T & string
      prefixKey?: KP
      labelKey: KeyUnderPrefix<KP>
      loadingId?: string
    }
  | {
      name: keyof T & string & KeyUnderPrefix<KP>
      prefixKey?: KP
      labelKey?: never
      loadingId?: string
    }

// Loose structural shape for the hook's constraint — pulling the discriminated
// FieldPrerequisiteSpec union through the generic trips TS2589.
export interface FieldPrerequisiteSpecShape<T extends FieldValues> {
  name: keyof T & string
  prefixKey?: TranslationKeyPrefix
  labelKey?: string
  loadingId?: string
}

type FieldValuesFromSpecs<T extends FieldValues, TFields extends readonly FieldPrerequisiteSpecShape<T>[]> = {
  [K in keyof TFields]: TFields[K] extends { name: infer N } ? (N extends keyof T ? T[N] : never) : never
}

export interface FieldPrerequisitesResult<TValues extends readonly unknown[] = readonly unknown[]> {
  isReady: boolean
  missing: string[]
  loading: string[]
  tooltip: string
  values: TValues
}

// Booleans are always considered present (a checkbox has no "unset" state).
const isMissingPrerequisite = (v: unknown): boolean => {
  if (v === undefined || v === null) return true
  if (typeof v === 'string') return v.trim() === ''
  if (typeof v === 'number') return Number.isNaN(v) || v <= 0
  return false
}

// Declare neighbor-field prerequisites for a worksheet field. Returns readiness,
// the watched values, and a translated tooltip listing what's blocking.
//
// `selfName` is the registry id under which this field re-broadcasts inherited
// prereq-loading, enabling dependency-chain propagation. Missing prereqs are
// NOT broadcast — downstream fields detect them via their own prereq check.
export const useFieldPrerequisites = <
  T extends FieldValues,
  const TFields extends readonly FieldPrerequisiteSpecShape<T>[],
>(
  control: Control<T>,
  fields: TFields,
  selfName: string,
): FieldPrerequisitesResult<FieldValuesFromSpecs<T, TFields>> => {
  const { t } = useTranslation()

  const names = useMemo(() => fields.map((f) => f.name) as FieldPath<T>[], [fields])
  const values = useWatch({ control, name: names }) as unknown as unknown[]

  const loadingIds = useMemo(() => fields.map((f) => f.loadingId ?? f.name), [fields])
  const loadingMap = useLoadingFor(loadingIds)

  const { missing, loading } = useMemo(() => {
    const m: string[] = []
    const l: string[] = []
    fields.forEach((field, i) => {
      const value = values[i]
      const prefix = field.prefixKey ?? 'worksheetCommon'
      const sub = field.labelKey ?? field.name
      // Cast: typed `t` returns `string | object` (returnObjects: true); our keys are leaves.
      const label = t(`${prefix}.${sub}` as ParseKeys<'translation'>) as string
      if (isMissingPrerequisite(value)) {
        m.push(label)
        return
      }
      const id = field.loadingId ?? field.name
      if (loadingMap[id]) {
        l.push(label)
      }
    })
    return { missing: m, loading: l }
  }, [fields, values, loadingMap, t])

  const isReady = missing.length === 0 && loading.length === 0

  useReportFieldLoading(selfName, loading.length > 0)

  const tooltip = useMemo(() => {
    if (missing.length > 0) {
      return t('worksheetCommon.fillPrerequisitesFirst', { fields: missing.join(', ') })
    }
    if (loading.length > 0) {
      return t('worksheetCommon.waitingForPrerequisites', { fields: loading.join(', ') })
    }
    return ''
  }, [missing, loading, t])
  return {
    isReady,
    missing,
    loading,
    tooltip,
    values: values as unknown as FieldValuesFromSpecs<T, TFields>,
  }
}

export interface AprCheckResult {
  tooHigh: boolean
  effectiveRate: number
  threshold: number
}

// Binding threshold: lower of the program legal cap and the province high-cost rate
// (currently Newfoundland only).
export const useAprCheck = (
  computedInfo: FundingComputedDto | null,
  stateIso: EProvince | undefined,
  effectiveRateThreshold: number,
  provinceConfigs: ProvinceConfigs,
): AprCheckResult =>
  useMemo(() => {
    const effectiveRate = computedInfo?.effectiveRate ?? 0

    const candidates: number[] = []
    if (effectiveRateThreshold > 0) candidates.push(effectiveRateThreshold)
    if (stateIso === EProvince.Newfoundland) {
      const provinceRate = provinceConfigs[stateIso]?.highCostRate
      if (provinceRate && provinceRate > 0) candidates.push(provinceRate)
    }

    const threshold = candidates.length > 0 ? Math.min(...candidates) : 0
    const tooHigh = !!computedInfo && threshold > 0 && effectiveRate >= threshold
    return { tooHigh, effectiveRate, threshold }
  }, [computedInfo, stateIso, effectiveRateThreshold, provinceConfigs])


/** @deprecated Use useAprCheck instead for the full APR check result. */
export const useAprTooHigh = (
  computedInfo: FundingComputedDto,
  stateIso: EProvince | undefined,
  provinceConfigs: ProvinceConfigs,
  financingConfig?: FinancingConfigDto,
): boolean => {
  const { tooHigh } = useAprCheck(
    computedInfo,
    stateIso,
    financingConfig?.effectiveRateThreshold ?? 0,
    provinceConfigs,
  )
  return tooHigh
}
